import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { PenTool, Book, Feather, Type, Quote, Scroll, Coffee, Highlighter } from 'lucide-react';

const CreativeWriting: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "The Writers' Workshop";
    
    return (
        <div className="min-h-screen bg-[#f7f5f0] font-serif text-[#2a2a2a] selection:bg-[#2a2a2a] selection:text-[#f7f5f0]">
            {/* Paper Texture Overlay */}
            <div className="fixed inset-0 opacity-[0.4] pointer-events-none z-0" style={{ 
                backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' 
            }}></div>

            {/* Top Bar */}
            <div className="relative z-50 text-center py-4 text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#888] border-b border-[#e5e0d6]">
                Accepting Manuscripts for Fall 2026
            </div>

            {/* Navbar */}
            <nav className="relative z-50 px-8 lg:px-24 py-8 flex justify-between items-center bg-[#f7f5f0]/80 backdrop-blur-sm">
                <span className="font-bold text-2xl tracking-tighter italic">{schoolName}</span>
                
                <div className="hidden md:flex gap-12 font-sans text-[10px] font-bold uppercase tracking-[0.15em] text-[#555]">
                    <a href="#" className="hover:text-black hover:underline decoration-1 underline-offset-4 transition-all">Fiction</a>
                    <a href="#" className="hover:text-black hover:underline decoration-1 underline-offset-4 transition-all">Poetry</a>
                    <a href="#" className="hover:text-black hover:underline decoration-1 underline-offset-4 transition-all">Non-Fiction</a>
                    <a href="#" className="hover:text-black hover:underline decoration-1 underline-offset-4 transition-all">Faculty</a>
                </div>

                <Button variant="ghost" className="hover:bg-transparent hover:text-black">
                     <Feather className="mr-2" size={16} /> Apply
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 py-32 px-8 lg:px-24 max-w-5xl mx-auto text-center md:text-left">
                <div className="flex flex-col md:flex-row gap-16 items-start">
                    <div className="flex-1">
                        <Quote size={48} className="text-[#e5e0d6] mb-8 mx-auto md:mx-0" fill="currentColor" />
                        
                        <h1 className="text-6xl md:text-8xl font-normal leading-none mb-12 tracking-tight">
                            Crafting the <br/>
                            <span className="italic font-light text-[#666]">Narrative.</span>
                        </h1>
                        
                        <p className="text-xl md:text-2xl font-light text-[#444] leading-relaxed mb-12">
                            A sanctuary for storytellers. Refine your voice under the guidance of acclaimed authors in an environment dedicated to the written word.
                        </p>
                        
                        <div className="flex flex-col md:flex-row gap-6">
                            <Button className="bg-[#2a2a2a] text-[#f7f5f0] hover:bg-[#444] font-sans font-bold uppercase tracking-widest text-xs h-14 px-10 rounded-sm">
                                Submit Portfolio
                            </Button>
                            <Button variant="outline" className="border-[#ccc] text-[#444] hover:border-[#2a2a2a] hover:bg-transparent font-sans font-bold uppercase tracking-widest text-xs h-14 px-10 rounded-sm">
                                View Workshops
                            </Button>
                        </div>
                    </div>

                    {/* Right Side Visual (Typewriter / Book aesthetic) */}
                    <div className="flex-1 w-full relative h-[500px] border-l border-[#e5e0d6] pl-16 hidden md:block">
                        <div className="absolute top-0 right-0 w-3/4 h-full bg-[#f0ebe0] p-8 shadow-sm rotate-2 hover:rotate-0 transition-transform duration-700 ease-out cursor-pointer group origin-bottom-right">
                             <div className="h-full border border-[#dcd6ca] p-8 flex flex-col items-center justify-center text-center">
                                 <Type size={32} className="text-[#888] mb-4 group-hover:text-black transition-colors" />
                                 <p className="font-serif italic text-lg leading-loose text-[#555]">
                                     "The scariest moment is always just before you start."
                                 </p>
                                 <div className="w-8 h-px bg-[#888] mt-4 mb-2"></div>
                                 <span className="font-sans text-[10px] uppercase tracking-widest text-[#888]">Stephen King</span>
                             </div>
                        </div>
                        <div className="absolute bottom-12 left-0 w-1/2 h-1/2 bg-white shadow-lg p-6 -rotate-3 hover:rotate-0 transition-transform duration-500 origin-bottom-left">
                            <div className="w-full h-full border-t-4 border-[#2a2a2a] p-4">
                                <span className="font-sans text-[10px] uppercase font-bold tracking-widest block mb-2">Upcoming Reading</span>
                                <h3 className="font-bold text-xl leading-tight">Poetry Slam: <br/> Voices of Tomorrow</h3>
                                <span className="text-xs text-[#666] mt-4 block">Oct 24 • The Library</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Courses / Genres */}
            <section className="relative z-10 py-24 px-8 border-y border-[#e5e0d6] bg-[#fffcf5]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                    <div className="md:col-span-1">
                        <h2 className="text-3xl font-bold mb-6">Syllabus</h2>
                        <p className="font-sans text-xs uppercase tracking-widest text-[#888] leading-relaxed">
                            Our curriculum is designed to challenge your perspective and hone your technical skills.
                        </p>
                    </div>
                    <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-8">
                        {[
                            { icon: Book, title: "Novel Writing", desc: "Structure, pacing, and character arcs." },
                            { icon: Scroll, title: "Screenwriting", desc: "Dialogue and visual storytelling." },
                            { icon: PenTool, title: "Editorial", desc: "Journalism and critical essays." },
                        ].map((item, i) => (
                            <div key={i} className="hover:bg-[#f7f5f0] p-6 -m-6 rounded-lg transition-colors cursor-pointer group">
                                <item.icon className="text-[#aaa] mb-4 group-hover:text-black transition-colors" size={24} />
                                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                                <p className="text-sm text-[#666] leading-relaxed serif">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="relative z-10 bg-[#2a2a2a] text-[#f7f5f0] py-24 px-8 lg:px-24">
                 <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-24">
                     <div>
                         <h2 className="text-4xl font-serif italic mb-8">{schoolName}</h2>
                         <div className="font-sans text-[10px] uppercase tracking-widest text-[#888] space-y-2">
                             <p>123 Inkwell Lane</p>
                             <p>London, UK</p>
                             <p>+44 20 7946 0123</p>
                         </div>
                     </div>
                     
                     <div className="flex gap-16 font-sans text-xs font-bold uppercase tracking-widest text-[#aaa]">
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white transition-colors">Fellowships</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Residencies</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Alumni</a></li>
                         </ul>
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white transition-colors">Submissions</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Events</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-7xl mx-auto mt-24 pt-8 border-t border-[#444] text-[10px] font-sans text-[#666] uppercase tracking-widest">
                     © 2026 {schoolName}. Write your legacy.
                 </div>
            </footer>
        </div>
    );
};

export default CreativeWriting;
